import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Search, Mail, Phone, Calendar, Award, 
  MoreVertical, UserCircle, Ban, CheckCircle, RefreshCw, Plus, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

// WebSocket URL helper
const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

const AdminUsersPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', skill_level: 'Beginner' });

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(
        `${API}/admin/users?skip=${pagination.skip}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Network error fetching users');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.skip, pagination.limit, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Realtime updates via WebSocket (auto-reconnect)
  useEffect(() => {
    if (!token) return;

    let ws;
    let reconnectTimer;
    let reconnectDelayMs = 2000;

    const connect = () => {
      try {
        ws = new WebSocket(getWebSocketUrl());

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (!msg || typeof msg !== 'object') return;

            if (msg.type === 'user_updated' && msg.user?.user_id) {
              setUsers((prev) => prev.map((u) => 
                u.user_id === msg.user.user_id ? msg.user : u
              ));
            }

            if (msg.type === 'user_blocked' && msg.user_id) {
              setUsers((prev) => prev.map((u) => 
                u.user_id === msg.user_id ? { ...u, is_blocked: true } : u
              ));
            }

            if (msg.type === 'user_unblocked' && msg.user_id) {
              setUsers((prev) => prev.map((u) => 
                u.user_id === msg.user_id ? { ...u, is_blocked: false } : u
              ));
            }
          } catch {
            // ignore malformed messages
          }
        };

        ws.onopen = () => {
          reconnectDelayMs = 2000;
        };

        ws.onclose = () => {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
            connect();
          }, reconnectDelayMs);
        };

        ws.onerror = () => {
          try { ws.close(); } catch { /* ignore */ }
        };
      } catch (error) {
        console.warn('WebSocket connection failed:', error);
      }
    };

    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [token]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const resetForm = () => setForm({ name: '', email: '', password: '', phone: '', skill_level: 'Beginner' });

  const saveUser = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    if (!form.password.trim()) { toast.error('Password is required'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('User created successfully');
        setShowModal(false);
        resetForm();
        setShowPassword(false);
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Error creating user');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_blocked: !currentStatus })
      });
      if (res.ok) {
        toast.success(`User ${currentStatus ? 'unblocked' : 'blocked'} successfully`);
        fetchUsers();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Network error updating user');
    }
  };

  const getSkillBadge = (level) => {
    const colors = {
      'Beginner': isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700',
      'Intermediate': isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700',
      'Advanced': isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700',
      'Pro': isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[level] || colors['Beginner']}`}>
        {level}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>User Management</h1>
            <p className={`${theme.textMuted} mt-1`}>{pagination.total} registered users</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setLoading(true); fetchUsers(); }} 
              className="border-slate-600"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {hasRole(['super_admin', 'manager']) && (
              <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-2" /> Add User
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <Card className={theme.card}>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${theme.textMuted}`} />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`pl-10 ${theme.input}`}
                />
              </div>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className={theme.card}>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
              </div>
            ) : users.length === 0 ? (
              <p className={`text-center py-12 ${theme.textMuted}`}>No users found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-sm border-b ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
                      <th className="p-4 font-medium">User</th>
                      <th className="p-4 font-medium">Contact</th>
                      <th className="p-4 font-medium">Skill Level</th>
                      <th className="p-4 font-medium">Membership</th>
                      <th className="p-4 font-medium">Points</th>
                      <th className="p-4 font-medium">Joined</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} className={`border-b text-sm ${isDarkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                              {user.picture ? (
                                <img src={user.picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <UserCircle className={`h-6 w-6 ${theme.textMuted}`} />
                              )}
                            </div>
                            <div>
                              <p className={`font-medium ${theme.text}`}>{user.name}</p>
                              <p className={`text-xs ${theme.textSubtle}`}>{user.user_id.slice(-8)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="space-y-1">
                            <p className={`flex items-center gap-1 ${theme.text}`}>
                              <Mail className="h-3 w-3" /> {user.email}
                            </p>
                            {user.phone && (
                              <p className={`flex items-center gap-1 text-xs ${theme.textMuted}`}>
                                <Phone className="h-3 w-3" /> {user.phone}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">{getSkillBadge(user.skill_level)}</td>
                        <td className="p-4">
                          <span className={theme.text}>{user.membership_type || 'None'}</span>
                        </td>
                        <td className="p-4">
                          <span className={`flex items-center gap-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                            <Award className="h-4 w-4" />
                            {user.loyalty_points || 0}
                          </span>
                        </td>
                        <td className={`p-4 ${theme.textMuted}`}>
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          {user.is_blocked ? (
                            <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>
                              <Ban className="h-3 w-3" /> Blocked
                            </span>
                          ) : (
                            <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              <CheckCircle className="h-3 w-3" /> Active
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          {hasRole(['super_admin', 'manager']) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleUserStatus(user.user_id, user.is_blocked)}
                              className={user.is_blocked ? 'text-emerald-400' : 'text-rose-400'}
                            >
                              {user.is_blocked ? 'Unblock' : 'Block'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className={`flex items-center justify-between p-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <p className={`text-sm ${theme.textMuted}`}>
                  Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.skip === 0}
                    onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                    className={theme.btnOutline}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.skip + pagination.limit >= pagination.total}
                    onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                    className={theme.btnOutline}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add User Modal */}
        <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) { resetForm(); setShowPassword(false); } }}>
          <DialogContent className={`${theme.card} max-w-md`}>
            <DialogHeader>
              <DialogTitle className={theme.text}>Add New User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className={theme.text}>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={`mt-1 ${theme.input}`}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label className={theme.text}>Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`mt-1 ${theme.input}`}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label className={theme.text}>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={`mt-1 pr-10 ${theme.input}`}
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.textMuted}`}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className={theme.text}>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={`mt-1 ${theme.input}`}
                  placeholder="+63 900 000 0000"
                />
              </div>
              <div>
                <Label className={theme.text}>Skill Level</Label>
                <select
                  value={form.skill_level}
                  onChange={(e) => setForm({ ...form, skill_level: e.target.value })}
                  className={`mt-1 w-full rounded-md border px-3 py-2 ${theme.input}`}
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Pro">Pro</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)} className={theme.btnOutline}>Cancel</Button>
              <Button onClick={saveUser} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
